import FormLayout from "src/layouts/FormLayout";
import EmploymentItem from "./EmploymentItem/EmploymentItem";
import EmploymentFormModal from "./EmploymentFormModal";
import { Col } from "src/components/layout";
import { useState } from "react";
import { useRedirectToPage, useSectionProgress } from "src/hooks";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import { useAuth } from "src/context";
import {
  fromEmploymentsList,
  type EmploymentFormData,
  type EmploymentListFormData,
} from "src/api/adapters/employment";
import {
  Add01Icon,
  Alert,
  FormTable,
  FormTableAddButton,
  useContainerRadiusSync,
  useFormTableAddButtonState,
  WorkHistoryIcon,
} from "@porchpass/ui";

export default () => {
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const [focusedEmployment, setFocusedEmployment] =
    useState<EmploymentFormData>();

  const sectionProgress = useSectionProgress();
  const {
    formData,
    loading: loadingForm,
    reloadFormData,
  } = useFormLoad<EmploymentListFormData>(
    {
      employments: [],
      hasSufficientEmploymentHistory: false,
    },
    () => api.getEmployments(),
    (data) =>
      fromEmploymentsList(data as Parameters<typeof fromEmploymentsList>[0]),
    { enabled: !!firebaseUser },
  );

  const addButtonState = useFormTableAddButtonState();
  const containerStyles = useContainerRadiusSync({
    adjacentToBottom: true,
    bottomExpanded: addButtonState.isExpanded,
  });

  const closeHandler = () => {
    setFocusedEmployment(undefined);
  };

  const confirmHandler = () => {
    setFocusedEmployment(undefined);
    reloadFormData();
  };

  const redirectToIncomePage = useRedirectToPage("income");
  const redirectToNoEmploymentPage = useRedirectToPage("no-employment");
  const redirectToProgressPage = useRedirectToPage("../../progress");

  const clickSubmitHandler = () => {
    if (
      formData.employments.length > 0 &&
      sectionProgress["employment"] &&
      sectionProgress["income"]
    ) {
      redirectToProgressPage();
    } else if (formData.employments.length > 0) {
      redirectToIncomePage();
    } else {
      redirectToNoEmploymentPage();
    }
  };

  const curCheckboxDisabled =
    formData.employments.filter((emp) => emp.is_current).length >= 2;
  let footerTitle = "Next";
  if (sectionProgress["employment"] && sectionProgress["income"])
    footerTitle = "Save";
  if (formData.employments.length === 0)
    footerTitle = "I'm currently not employed";
  return (
    <FormLayout
      headerTitle="Employment"
      title="Employment Information"
      subtitle="Please add your current and past employment."
      stepIndex={3}
      footerTitle={footerTitle}
      clickHandler={() => void clickSubmitHandler()}
      isLoading={loadingForm}
    >
      <Col>
        <Alert variant="info">
          We recommend providing at least 3 years of employment history to
          improve your chances.
        </Alert>

        <FormTable
          title="Employment History"
          headerIcon={
            <WorkHistoryIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
          style={containerStyles.style}
        >
          {formData.employments &&
            formData.employments.map((employment_detail, index) => (
              <EmploymentItem
                key={employment_detail.id}
                index={index}
                employment_detail={employment_detail}
                editHandler={() => setFocusedEmployment(employment_detail)}
                closeHandler={closeHandler}
                deleteHandler={confirmHandler}
              />
            ))}

          <FormTableAddButton
            type="button"
            onClick={() => setFocusedEmployment({ id: "CREATE" })}
            {...addButtonState.interactionProps}
          >
            <Add01Icon variant="stroke-rounded" size={16} />
            {formData.employments.length === 0
              ? "Add employment"
              : "Additional employment"}
          </FormTableAddButton>
        </FormTable>
      </Col>

      {focusedEmployment && (
        <EmploymentFormModal
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
          employment_detail={focusedEmployment}
          hideCurrentCheckbox={curCheckboxDisabled}
        />
      )}
    </FormLayout>
  );
};
